import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, Lock, ArrowLeft, LogIn, UserPlus, Mail, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Google icon SVG
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

export default function UserLogin() {
  const navigate = useNavigate();
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [loginCode, setLoginCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [googleLoginEnabled, setGoogleLoginEnabled] = useState(true);

  // Check if already logged in as customer and fetch auth settings
  useEffect(() => {
    const customerToken = localStorage.getItem("customer_token");
    if (customerToken) {
      navigate("/pelanggan", { replace: true });
    }

    // Fetch auth settings
    const fetchAuthSettings = async () => {
      try {
        const res = await axios.get(`${API}/auth-settings`);
        setGoogleLoginEnabled(res.data.google_login_enabled !== false);
      } catch (err) {
        console.error("Failed to fetch auth settings:", err);
      }
    };
    fetchAuthSettings();
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!phone.trim() || !password.trim()) {
      toast.error("Isi semua field");
      return;
    }
    setIsLoading(true);
    try {
      // Build a minimal device_info for backend logging
      const device_info = `${navigator.platform || "?"} | ${(navigator.userAgent || "").slice(0, 120)}`;
      // Kode login opsional (User Spesial boleh kosong). Backend akan menolak jika bukan spesial dan kode kosong.
      const res = await axios.post(`${API}/auth/login`, {
        phone: phone.trim(),
        password,
        login_code: loginCode.trim() ? loginCode.trim().toUpperCase() : "",
        device_info
      });
      localStorage.setItem("user_token", res.data.access_token);
      localStorage.setItem("user_name", res.data.user_name);
      localStorage.setItem("user_role", res.data.role);
      localStorage.setItem("user_balance", res.data.balance);
      localStorage.setItem("user_phone", phone.trim()); // Simpan phone untuk verifikasi offline
      toast.success(`Selamat datang, ${res.data.user_name}!`);
      
      // Redirect based on role
      if (res.data.role === "pengawas") {
        navigate("/pengawas");
      } else if (res.data.role === "petugas_lapangan") {
        // Petugas lapangan masuk ke landing yang sudah filter read-only
        navigate("/");
      } else {
        navigate("/");
      }
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || "Login gagal";
      if (status === 409) {
        // Already logged in on another device
        toast.error(detail, { duration: 6000 });
      } else {
        toast.error(detail);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Google Login
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen hero-gradient flex flex-col">
      <header className="container mx-auto px-6 py-6">
        <nav className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/icons/icon-72x72.png" alt="Logo" className="w-10 h-10 rounded-xl" />
            <span className="text-xl font-heading font-semibold text-slate-800">Produk Digital</span>
          </div>
          <Link to="/">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Beranda
            </Button>
          </Link>
        </nav>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 pb-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="w-full max-w-md"
        >
          <Card className="glass-card-solid card-hover">
            <CardContent className="p-8">
              <motion.div 
                className="text-center mb-8"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
              >
                <motion.div 
                  className="w-16 h-16 rounded-2xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center mx-auto mb-4"
                  initial={{ rotate: -10, scale: 0.8 }}
                  animate={{ rotate: 0, scale: 1 }}
                  transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                  whileHover={{ scale: 1.1, rotate: 5 }}
                >
                  <LogIn className="w-8 h-8 text-white" />
                </motion.div>
                <h1 className="text-2xl font-heading font-bold text-slate-900">Login</h1>
                <p className="text-slate-500 text-sm mt-1">Masuk ke akun petugas / agen</p>
              </motion.div>

              <form onSubmit={handleLogin} className="space-y-4">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">No. HP</label>
                  <div className="relative">
                    <Input
                      type="tel"
                      placeholder="08xxxxxxxxxx"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="input-animated h-12 pl-11 rounded-xl"
                      data-testid="login-phone-input"
                    />
                    <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-slate-400" />
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Password</label>
                  <div className="relative">
                    <Input
                      type="password"
                      placeholder="Masukkan password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="input-animated h-12 pl-11 rounded-xl"
                      data-testid="login-password-input"
                    />
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-slate-400" />
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.55 }}
                >
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                    Kode Login <span className="text-slate-400 text-xs font-normal">(opsional untuk User Spesial)</span>
                  </label>
                  <div className="relative">
                    <Input
                      type="text"
                      placeholder="6 karakter dari admin (kosongkan jika User Spesial)"
                      value={loginCode}
                      onChange={(e) => setLoginCode(e.target.value.toUpperCase().slice(0, 8))}
                      className="input-animated h-12 pl-11 rounded-xl font-mono tracking-widest uppercase"
                      maxLength={8}
                      data-testid="login-code-input"
                    />
                    <KeyRound className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-slate-400" />
                  </div>
                  <p className="text-xs text-slate-500 mt-1.5">
                    Minta kode login (berlaku 15 menit) dari admin Anda. <span className="text-emerald-600 font-medium">User Spesial</span> bisa login langsung tanpa kode.
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="btn-animated w-full h-12 rounded-full text-base font-semibold bg-gradient-to-r from-sky-500 to-blue-600 shadow-lg hover:shadow-xl"
                    data-testid="login-submit-btn"
                  >
                    {isLoading ? <div className="spinner w-5 h-5" /> : "Masuk"}
                  </Button>
                </motion.div>
              </form>

              <motion.div 
                className="mt-6 text-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
              >
                <p className="text-sm text-slate-500">
                  Belum punya akun?{" "}
                  <Link to="/register" className="text-sky-600 font-medium hover:underline" data-testid="register-link">
                    Daftar di sini
                  </Link>
                </p>
              </motion.div>

              {/* Divider */}
              {googleLoginEnabled && (
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200"></div>
                  </div>
                  <div className="relative flex justify-center">
                    <span className="bg-white px-3 text-sm text-slate-500">atau</span>
                  </div>
                </div>
              )}

              {/* Google Login for Pelanggan */}
              {googleLoginEnabled ? (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                >
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleGoogleLogin}
                    className="w-full h-12 rounded-full gap-3 border-slate-300 hover:bg-slate-50 text-slate-700"
                    data-testid="google-login-btn"
                  >
                    <GoogleIcon />
                    Masuk dengan Google
                  </Button>
                  <p className="text-xs text-slate-400 text-center mt-2">
                    Untuk pelanggan - langsung aktif tanpa persetujuan admin
                  </p>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                  className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center"
                >
                  <p className="text-sm text-amber-800">
                    Login dengan Google saat ini dinonaktifkan. Silakan login dengan nomor HP dan password.
                  </p>
                </motion.div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </main>
    </div>
  );
}
